"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateAndLogErrors = exports.stringToMD5 = exports.genGuid = exports.buildFacets = exports.facetsCoercion = exports.timestamp = void 0;
const Logger_1 = __importDefault(require("./Logger"));
const Common_1 = require("./Common");
/**
 * The current timestamp
 * @returns {string}
 * @private
 */
function timestamp(date) {
    const tzo = -date.getTimezoneOffset();
    const dif = tzo >= 0 ? "+" : "-";
    const pad = (num, length = 2) => {
        const norm = Math.abs(Math.floor(num));
        return norm.toString().padStart(length, "0");
    };
    const t0 = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    const t1 = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    const t2 = `${pad(date.getMilliseconds(), 3)}${dif}${pad(tzo / 60)}:${pad(tzo % 60)}`;
    return `${t0}T${t1}.${t2}`;
}
exports.timestamp = timestamp;
/**
 * Force map's values to be of String type
 * @param map
 * @private
 */
function _forceMapValuesToString(map) {
    if (map) {
        Object.keys(map).forEach((key) => {
            if (map[key]) {
                map[key] = `${map[key]}`;
            }
        });
    }
}
/**
 * Force the facet's map attributes to fulfill the desired field spec
 * @param fts facets
 * @private
 */
function facetsCoercion(fts) {
    var _a;
    if (fts) {
        if ((_a = fts.product) === null || _a === void 0 ? void 0 : _a.id_provider) {
            fts.product.id_provider = fts.product.id_provider.toLocaleLowerCase();
        }
        if (fts.operation) {
            _forceMapValuesToString(fts.operation.meta);
        }
        if (fts.entitlement) {
            _forceMapValuesToString(fts.entitlement.properties);
        }
        if (fts.component) {
            _forceMapValuesToString(fts.component.attributes);
            _forceMapValuesToString(fts.component.metrics);
        }
        if (fts.content) {
            _forceMapValuesToString(fts.content.meta);
        }
        if (fts.http) {
            _forceMapValuesToString(fts.http.form_params);
            _forceMapValuesToString(fts.http.request_headers);
            _forceMapValuesToString(fts.http.response_headers);
        }
    }
}
exports.facetsCoercion = facetsCoercion;
/**
 * Builds the facets
 * @param fts the facets to set
 * @param ctxFts the facets that are already in place in the SDK context
 * @private
 */
function buildFacets(fts, ctxFts) {
    var _a;
    const facets = (ctxFts !== null && ctxFts !== void 0 ? ctxFts : {});
    if (fts) {
        facetsCoercion(fts);
        if (fts.browser)
            facets.browser = fts.browser;
        if (fts.geo)
            facets.geo = fts.geo;
        if (fts.product)
            facets.product = fts.product;
        if (fts.device)
            facets.device = fts.device;
        if (fts.operation)
            facets.operation = fts.operation;
        if (fts.entitlement)
            facets.entitlement = fts.entitlement;
        if (fts.component)
            facets.component = fts.component;
        if (fts.content)
            facets.content = fts.content;
        if (fts.http)
            facets.http = fts.http;
        if (fts.experiment)
            facets.experiment = fts.experiment;
        if (fts.search)
            facets.search = fts.search;
        if (fts.page)
            facets.page = fts.page;
        facets.user = (_a = facets.user) !== null && _a !== void 0 ? _a : { info: [] };
    }
    return facets;
}
exports.buildFacets = buildFacets;
/**
 * Generates an GUID RFC-4122 compliant
 * @returns {string}
 * @private
 */
function genGuid() {
    if (crypto === null || crypto === void 0 ? void 0 : crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // fallback if crypto.randomUUID is not supported
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        // eslint-disable-next-line no-bitwise
        const r = (Math.random() * 16) | 0;
        // eslint-disable-next-line no-bitwise
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
exports.genGuid = genGuid;
/* MD5 Helper functions */
const safe_add = (x, y) => {
    const a = (x & 0xffff) + (y & 0xffff);
    const b = (x >> 16) + (y >> 16) + (a >> 16);
    return (b << 16) | (a & 0xffff);
};
const bit_rol = (a, b) => {
    return (a << b) | (a >>> (32 - b));
};
const md5_cmn = (q, a, b, x, s, t) => {
    return safe_add(bit_rol(safe_add(safe_add(a, q), safe_add(x, t)), s), b);
};
const md5_ff = (a, b, c, d, x, s, t) => {
    return md5_cmn((b & c) | (~b & d), a, b, x, s, t);
};
const md5_gg = (a, b, c, d, x, s, t) => {
    return md5_cmn((b & d) | (c & ~d), a, b, x, s, t);
};
const md5_hh = (a, b, c, d, x, s, t) => {
    return md5_cmn(b ^ c ^ d, a, b, x, s, t);
};
const md5_ii = (a, b, c, d, x, s, t) => {
    return md5_cmn(c ^ (b | ~d), a, b, x, s, t);
};
/**
 * MD5 Checksum calculation, generates a MD5Hash out of a string provided
 * @param {string} str required, string to hash
 * @return {string}
 */
function stringToMD5(str) {
    const hexcase = 0;
    const chrsz = 8;
    const cmc5 = (x, e) => {
        x[e >> 5] |= 0x80 << e % 32;
        x[(((e + 64) >>> 9) << 4) + 14] = e;
        let a = 1732584193;
        let b = -271733879;
        let c = -1732584194;
        let d = 271733878;
        for (let i = 0; i < x.length; i += 16) {
            const f = a;
            const g = b;
            const h = c;
            const j = d;
            a = md5_ff(a, b, c, d, x[i + 0], 7, -680876936);
            d = md5_ff(d, a, b, c, x[i + 1], 12, -389564586);
            c = md5_ff(c, d, a, b, x[i + 2], 17, 606105819);
            b = md5_ff(b, c, d, a, x[i + 3], 22, -1044525330);
            a = md5_ff(a, b, c, d, x[i + 4], 7, -176418897);
            d = md5_ff(d, a, b, c, x[i + 5], 12, 1200080426);
            c = md5_ff(c, d, a, b, x[i + 6], 17, -1473231341);
            b = md5_ff(b, c, d, a, x[i + 7], 22, -45705983);
            a = md5_ff(a, b, c, d, x[i + 8], 7, 1770035416);
            d = md5_ff(d, a, b, c, x[i + 9], 12, -1958414417);
            c = md5_ff(c, d, a, b, x[i + 10], 17, -42063);
            b = md5_ff(b, c, d, a, x[i + 11], 22, -1990404162);
            a = md5_ff(a, b, c, d, x[i + 12], 7, 1804603682);
            d = md5_ff(d, a, b, c, x[i + 13], 12, -40341101);
            c = md5_ff(c, d, a, b, x[i + 14], 17, -1502002290);
            b = md5_ff(b, c, d, a, x[i + 15], 22, 1236535329);
            a = md5_gg(a, b, c, d, x[i + 1], 5, -165796510);
            d = md5_gg(d, a, b, c, x[i + 6], 9, -1069501632);
            c = md5_gg(c, d, a, b, x[i + 11], 14, 643717713);
            b = md5_gg(b, c, d, a, x[i + 0], 20, -373897302);
            a = md5_gg(a, b, c, d, x[i + 5], 5, -701558691);
            d = md5_gg(d, a, b, c, x[i + 10], 9, 38016083);
            c = md5_gg(c, d, a, b, x[i + 15], 14, -660478335);
            b = md5_gg(b, c, d, a, x[i + 4], 20, -405537848);
            a = md5_gg(a, b, c, d, x[i + 9], 5, 568446438);
            d = md5_gg(d, a, b, c, x[i + 14], 9, -1019803690);
            c = md5_gg(c, d, a, b, x[i + 3], 14, -187363961);
            b = md5_gg(b, c, d, a, x[i + 8], 20, 1163531501);
            a = md5_gg(a, b, c, d, x[i + 13], 5, -1444681467);
            d = md5_gg(d, a, b, c, x[i + 2], 9, -51403784);
            c = md5_gg(c, d, a, b, x[i + 7], 14, 1735328473);
            b = md5_gg(b, c, d, a, x[i + 12], 20, -1926607734);
            a = md5_hh(a, b, c, d, x[i + 5], 4, -378558);
            d = md5_hh(d, a, b, c, x[i + 8], 11, -2022574463);
            c = md5_hh(c, d, a, b, x[i + 11], 16, 1839030562);
            b = md5_hh(b, c, d, a, x[i + 14], 23, -35309556);
            a = md5_hh(a, b, c, d, x[i + 1], 4, -1530992060);
            d = md5_hh(d, a, b, c, x[i + 4], 11, 1272893353);
            c = md5_hh(c, d, a, b, x[i + 7], 16, -155497632);
            b = md5_hh(b, c, d, a, x[i + 10], 23, -1094730640);
            a = md5_hh(a, b, c, d, x[i + 13], 4, 681279174);
            d = md5_hh(d, a, b, c, x[i + 0], 11, -358537222);
            c = md5_hh(c, d, a, b, x[i + 3], 16, -722521979);
            b = md5_hh(b, c, d, a, x[i + 6], 23, 76029189);
            a = md5_hh(a, b, c, d, x[i + 9], 4, -640364487);
            d = md5_hh(d, a, b, c, x[i + 12], 11, -421815835);
            c = md5_hh(c, d, a, b, x[i + 15], 16, 530742520);
            b = md5_hh(b, c, d, a, x[i + 2], 23, -995338651);
            a = md5_ii(a, b, c, d, x[i + 0], 6, -198630844);
            d = md5_ii(d, a, b, c, x[i + 7], 10, 1126891415);
            c = md5_ii(c, d, a, b, x[i + 14], 15, -1416354905);
            b = md5_ii(b, c, d, a, x[i + 5], 21, -57434055);
            a = md5_ii(a, b, c, d, x[i + 12], 6, 1700485571);
            d = md5_ii(d, a, b, c, x[i + 3], 10, -1894986606);
            c = md5_ii(c, d, a, b, x[i + 10], 15, -1051523);
            b = md5_ii(b, c, d, a, x[i + 1], 21, -2054922799);
            a = md5_ii(a, b, c, d, x[i + 8], 6, 1873313359);
            d = md5_ii(d, a, b, c, x[i + 15], 10, -30611744);
            c = md5_ii(c, d, a, b, x[i + 6], 15, -1560198380);
            b = md5_ii(b, c, d, a, x[i + 13], 21, 1309151649);
            a = md5_ii(a, b, c, d, x[i + 4], 6, -145523070);
            d = md5_ii(d, a, b, c, x[i + 11], 10, -1120210379);
            c = md5_ii(c, d, a, b, x[i + 2], 15, 718787259);
            b = md5_ii(b, c, d, a, x[i + 9], 21, -343485551);
            a = safe_add(a, f);
            b = safe_add(b, g);
            c = safe_add(c, h);
            d = safe_add(d, j);
        }
        return [a, b, c, d];
    };
    const s2b = (a) => {
        const b = [];
        const c = (1 << chrsz) - 1;
        for (let i = 0; i < a.length * chrsz; i += chrsz)
            b[i >> 5] |= (a.charCodeAt(i / chrsz) & c) << i % 32;
        return b;
    };
    const b2h = (a) => {
        const b = hexcase ? "0123456789ABCDEF" : "0123456789abcdef";
        let c = "";
        for (let i = 0; i < a.length * 4; i += 1) {
            c += b.charAt((a[i >> 2] >> ((i % 4) * 8 + 4)) & 0xf) + b.charAt((a[i >> 2] >> ((i % 4) * 8)) & 0xf);
        }
        return c;
    };
    return b2h(cmc5(s2b(str), str.length * chrsz));
}
exports.stringToMD5 = stringToMD5;
function validateAndLogErrors(args, struct) {
    const [errors] = struct.validate(args);
    if (errors) {
        Logger_1.default.error(Common_1.ERROR_MESSAGE.invalidArguments, errors.message);
    }
    return !errors;
}
exports.validateAndLogErrors = validateAndLogErrors;
